import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Ambiguity,
  ProposedResolution,
  ResolutionSource,
  StoryHistory,
  TextPatch,
} from '../../../../shared/templateGen/templateInterfaces';
import { StoryHistoryService } from '../../../../shared/services/story-history.service';
import { StoryPatchService, MatchResult } from '../../../../shared/services/story-patch.service';

export interface HighlightSegment {
  text: string;
  isHighlighted: boolean;
  highlightClass?: string;
  ambiguity?: Ambiguity;
  resolution?: ProposedResolution;
  patch?: TextPatch;
}

@Component({
  selector: 'app-story-interactive',
  templateUrl: './story-interactive.component.html',
  styleUrls: ['./story-interactive.component.scss'],
})
export class StoryInteractiveComponent implements OnInit, OnDestroy, OnChanges {
  @Input() storyText = '';
  @Input() ambiguities: Ambiguity[] = [];
  @Input() resolutions: ProposedResolution[] = [];

  private destroy$ = new Subject<void>();

  // Rendered segments
  segments: HighlightSegment[] = [];

  // Sidebar state
  selectedAmbiguity: Ambiguity | null = null;
  selectedResolution: ProposedResolution | null = null;
  sidebarVisible = false;

  // History navigation
  history: StoryHistory | null = null;
  currentTurnDisplay = 'Originale';

  // Edit state
  editingPatch = false;
  editText = '';

  constructor(
    private historyService: StoryHistoryService,
    private patchService: StoryPatchService,
  ) {}

  ngOnInit(): void {
    this.historyService.history$.pipe(takeUntil(this.destroy$)).subscribe(h => {
      this.history = h;
      if (h) {
        this.currentTurnDisplay = h.current_turn < 0
          ? 'Originale'
          : `Turno ${h.current_turn + 1}`;
        this._buildSegments(h.concorded_story);
      }
    });

    if (this.storyText) {
      this.historyService.initialize(this.storyText);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ambiguities'] || changes['resolutions']) {
      const text = this.history?.concorded_story || this.storyText;
      this._buildSegments(text);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Segment click ---

  onSegmentClick(segment: HighlightSegment): void {
    if (!segment.isHighlighted) {
      this.closeSidebar();
      return;
    }
    this.selectedAmbiguity = segment.ambiguity || null;
    this.selectedResolution = segment.resolution || null;
    this.sidebarVisible = true;
  }

  closeSidebar(): void {
    this.sidebarVisible = false;
    this.selectedAmbiguity = null;
    this.selectedResolution = null;
    this.editingPatch = false;
  }

  // --- Resolution actions from sidebar ---

  approveResolution(): void {
    if (!this.selectedResolution || !this.selectedAmbiguity) return;
    this.selectedResolution.user_action = 'approved';

    const patch: TextPatch = {
      patch_id: `patch-${Date.now()}`,
      ambiguity_id: this.selectedAmbiguity.id,
      original_text: this.selectedAmbiguity.source,
      enriched_text: this.selectedResolution.proposed_text,
      patch_type: 'annotate',
      resolution_source: 'auto_resolve',
      confidence: this.selectedResolution.confidence,
      kb_sources: this.selectedResolution.sources,
      turn: this.historyService.getTurnCount(),
      created_at: new Date().toISOString(),
      created_by: 'agent',
    };

    this.historyService.addTurn(
      [patch],
      'user_approve',
      `Approvata risoluzione per ${this.selectedAmbiguity.id}`,
    );
  }

  startEdit(): void {
    this.editingPatch = true;
    this.editText = this.selectedResolution?.proposed_text || '';
  }

  saveEdit(): void {
    if (!this.selectedResolution || !this.selectedAmbiguity) return;
    this.selectedResolution.user_action = 'modified';
    this.selectedResolution.user_modified_text = this.editText;

    const patch: TextPatch = {
      patch_id: `patch-${Date.now()}`,
      ambiguity_id: this.selectedAmbiguity.id,
      original_text: this.selectedAmbiguity.source,
      enriched_text: this.editText,
      patch_type: 'append',
      resolution_source: 'user_edit',
      turn: this.historyService.getTurnCount(),
      created_at: new Date().toISOString(),
      created_by: 'user',
    };

    this.historyService.addTurn(
      [patch],
      'user_edit',
      `Modificata risoluzione per ${this.selectedAmbiguity.id}`,
    );
    this.editingPatch = false;
  }

  rejectResolution(): void {
    if (!this.selectedResolution) return;
    this.selectedResolution.user_action = 'rejected';
    this._buildSegments(this.history?.concorded_story || this.storyText);
  }

  // --- History navigation ---

  previousTurn(): void {
    this.historyService.previous();
  }

  nextTurn(): void {
    this.historyService.next();
  }

  // --- Helpers ---

  getHighlightClass(amb: Ambiguity | undefined, res: ProposedResolution | undefined): string {
    if (!res || !res.user_action || res.user_action === 'pending') {
      if (res) return 'highlight-proposed';
      return 'highlight-unresolved';
    }
    switch (res.user_action) {
      case 'approved': return 'highlight-approved';
      case 'modified': return 'highlight-modified';
      case 'rejected': return 'highlight-unresolved';
      default: return 'highlight-unresolved';
    }
  }

  getConfidenceClass(confidence: string | undefined): string {
    switch (confidence) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return '';
    }
  }

  get hasTurns(): boolean {
    return (this.history?.turns.length || 0) > 0;
  }

  get canGoPrevious(): boolean {
    return (this.history?.current_turn ?? -1) > -1;
  }

  get canGoNext(): boolean {
    if (!this.history) return false;
    return this.history.current_turn < this.history.turns.length - 1;
  }

  get summaryText(): string {
    const resolved = this.resolutions.filter(r => r.user_action === 'approved' || r.user_action === 'modified').length;
    const total = this.ambiguities.length;
    const unresolved = total - resolved;
    return `${total} ambiguita', ${resolved} risolte, ${unresolved} in attesa`;
  }

  // --- Build highlight segments ---

  private _buildSegments(text: string): void {
    if (!text) {
      this.segments = [];
      return;
    }

    // Collect all matches with their positions
    const matches: Array<{
      start: number;
      end: number;
      ambiguity: Ambiguity;
      resolution?: ProposedResolution;
    }> = [];

    for (const amb of this.ambiguities) {
      const match = this.patchService.findBestMatch(amb.source, text);
      if (match && match.confidence >= 0.5) {
        const resolution = this.resolutions.find(r => r.ambiguity_id === amb.id);
        matches.push({ start: match.start, end: match.end, ambiguity: amb, resolution });
      }
    }

    // Sort by start position, resolve overlaps (prefer earlier/longer)
    matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

    const segments: HighlightSegment[] = [];
    let cursor = 0;

    for (const m of matches) {
      if (m.start < cursor) continue; // skip overlapping

      // Add non-highlighted text before this match
      if (m.start > cursor) {
        segments.push({ text: text.slice(cursor, m.start), isHighlighted: false });
      }

      // Add highlighted segment
      const hlClass = this.getHighlightClass(m.ambiguity, m.resolution);
      segments.push({
        text: text.slice(m.start, m.end),
        isHighlighted: true,
        highlightClass: hlClass,
        ambiguity: m.ambiguity,
        resolution: m.resolution,
      });

      cursor = m.end;
    }

    // Add remaining text
    if (cursor < text.length) {
      segments.push({ text: text.slice(cursor), isHighlighted: false });
    }

    this.segments = segments;
  }
}
